import { Link } from "react-router-dom";
import { Heart, Trash2 } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useWishlist } from "@/context/WishlistContext";
import { getRecents } from "@/lib/recents";
import { inr } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

function Row({ item, onRemove, close }) {
  return (
    <li className="flex items-center gap-3 py-3 border-b border-brand/10" data-testid={`drawer-item-${item.slug}`}>
      <Link to={`/products/${item.slug}`} onClick={close}>
        <img src={item.image} alt="" className="h-14 w-14 object-cover bg-white border border-brand/10 rounded-lg" />
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/products/${item.slug}`} onClick={close} className="text-sm font-medium line-clamp-1 hover:text-brand transition-colors">
          {item.title}
        </Link>
        <p className="text-[11px] font-mono text-ink-2">{item.purity} {item.metal}</p>
      </div>
      {item.price_min != null && <span className="text-sm font-semibold gold-num">{inr(item.price_min)}</span>}
      {onRemove && (
        <button onClick={() => onRemove(item)} className="text-ink-2 hover:text-red-600" aria-label="Remove" data-testid={`drawer-remove-${item.slug}`}>
          <Trash2 size={15} />
        </button>
      )}
    </li>
  );
}

export default function WishlistDrawer() {
  const { wishlistOpen, setWishlistOpen } = useStore();
  const { items, toggle } = useWishlist();
  const recents = getRecents();
  const close = () => setWishlistOpen(false);

  return (
    <Sheet open={wishlistOpen} onOpenChange={setWishlistOpen}>
      <SheetContent className="w-full sm:max-w-md bg-ivory p-0 flex flex-col" data-testid="wishlist-drawer">
        <SheetHeader className="px-5 py-4 border-b border-brand/10 bg-white">
          <SheetTitle className="font-heading flex items-center gap-2"><Heart size={17} className="text-gold" /> Saved</SheetTitle>
        </SheetHeader>
        <Tabs defaultValue="wishlist" className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-5 mt-4 grid grid-cols-2">
            <TabsTrigger value="wishlist" data-testid="drawer-tab-wishlist">Wishlist ({items.length})</TabsTrigger>
            <TabsTrigger value="recents" data-testid="drawer-tab-recents">Recently Viewed</TabsTrigger>
          </TabsList>
          <TabsContent value="wishlist" className="flex-1 overflow-y-auto px-5 pb-5">
            {items.length === 0 ? (
              <div className="text-center py-14" data-testid="wishlist-empty">
                <p className="text-sm text-ink-2">Nothing saved yet.</p>
                <Button variant="outline" className="mt-4 rounded-full" onClick={close} data-testid="wishlist-browse-btn">
                  <Link to="/shop">Browse jewellery</Link>
                </Button>
              </div>
            ) : (
              <ul>{items.map((i) => <Row key={i.id} item={i} onRemove={toggle} close={close} />)}</ul>
            )}
          </TabsContent>
          <TabsContent value="recents" className="flex-1 overflow-y-auto px-5 pb-5">
            {recents.length === 0 ? (
              <p className="text-sm text-ink-2 text-center py-14" data-testid="recents-empty">Pieces you view will appear here.</p>
            ) : (
              <ul>{recents.map((i) => <Row key={i.id} item={i} close={close} />)}</ul>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
